import { arrow, autoPlacement, offset, Placement, shift, Strategy, useFloating } from '@floating-ui/react-dom';
import { Popover } from '@headlessui/react';
import cx from 'classnames';
import { createContext, useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'components/Icon';

export type Props = {
  children: React.ReactNode;
};

type BasePlacement = 'top' | 'left' | 'bottom' | 'right';

const TOOLTIP_OFFSET = 10;
const ARROW_SIZE = 8;
const ARROW_OFFSET = ARROW_SIZE / -2;
const ARROW_PADDING = 12;
const ARROW_SIZE_STYLE = {
  height: ARROW_SIZE,
  width: ARROW_SIZE,
};

const sides: Record<BasePlacement, BasePlacement> = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right',
};

type TooltipCtx = {
  open: boolean;
  close: () => void;
  triggerRef: (node: HTMLElement | null) => void;
  tooltipRef: (node: HTMLElement | null) => void;
  tooltipStyle: { position: Strategy; top: number | string; left: number | string };
  arrowRef: React.Ref<HTMLDivElement>;
  arrowStyle: {
    left: string;
    top: string;
    right: string;
    bottom: string;
  };
};

const TooltipContext = createContext<TooltipCtx | null>(null);

const useTooltipContext = () => {
  const ctx = useContext(TooltipContext);

  if (!ctx) {
    throw new Error(`You must wrap <Tooltip.Trigger> and <Tooltip.Content> in <Tooltip>!`);
  }

  return ctx;
};

const getStaticSide = (placement: Placement): BasePlacement => {
  return sides[placement.split('-')[0] as BasePlacement];
};

export const Tooltip = ({ children }: Props): JSX.Element => {
  const arrowRef = useRef<HTMLDivElement | null>(null);

  const { x, y, reference, floating, strategy, placement, middlewareData } = useFloating({
    placement: 'bottom',
    middleware: [
      offset({ mainAxis: TOOLTIP_OFFSET }),
      autoPlacement({ allowedPlacements: ['top', 'bottom'] }),
      shift(),
      arrow({ element: arrowRef, padding: ARROW_PADDING }),
    ],
  });

  const { x: arrowX, y: arrowY } = middlewareData?.arrow || { x: null, y: null, centerOffset: null };
  const staticSide = getStaticSide(placement);

  const context = useCallback(
    (open: boolean, close: () => void) => {
      const tooltipStyle = {
        position: strategy,
        top: y ?? '',
        left: x ?? '',
      };

      const arrowStyle = {
        left: arrowX != null ? `${arrowX}px` : '',
        top: arrowY != null ? `${arrowY}px` : '',
        right: '',
        bottom: '',
        [staticSide]: `${ARROW_OFFSET}px`,
      };

      return {
        open,
        close,
        arrowStyle,
        arrowRef,
        tooltipStyle,
        tooltipRef: floating,
        triggerRef: reference,
      };
    },
    [strategy, x, y, arrowX, arrowY, staticSide, arrowRef, floating, reference],
  );

  return (
    <Popover className="relative">
      {({ open, close }) => {
        return <TooltipContext.Provider value={context(open, close)}>{children}</TooltipContext.Provider>;
      }}
    </Popover>
  );
};

type TooltipTriggerProps = {
  className?: string;
  children: React.ReactNode;
};

Tooltip.Trigger = function TooltipTrigger({ className, children }: TooltipTriggerProps) {
  const { triggerRef } = useTooltipContext();

  return (
    <Popover.Button ref={triggerRef} className={className}>
      {children}
    </Popover.Button>
  );
};

type TooltipContentProps = {
  children: React.ReactNode;
  className?: string;
  overrideClassName?: string;
};

Tooltip.Content = function TooltipContent({ children, className, overrideClassName }: TooltipContentProps) {
  const { t } = useTranslation();
  const { open, close, tooltipRef, tooltipStyle, arrowRef, arrowStyle } = useTooltipContext();

  return (
    <Popover.Panel
      ref={tooltipRef}
      style={tooltipStyle}
      aria-live="polite"
      className={cx(
        className,
        overrideClassName || 'p-16 max-w-[300px] bg-black-900 text-white rounded-md shadow-lg shadow-black/30 z-10',
        {
          'animate-tooltip-appear': open,
        },
      )}
    >
      <div className="absolute top-8 right-8">
        <button type="button" onClick={close} className="sr-only focus:!not-sr-only text-white">
          <Icon name="Times" title={t('tooltip.dismissButtonText')} />
        </button>
      </div>

      <div ref={arrowRef} className="absolute rotate-45 bg-black-900" style={{ ...ARROW_SIZE_STYLE, ...arrowStyle }} />

      {/* @NOTE(shawk): the tabindex allows for selecting text within tooltips without
          accidentally dismissing them */}
      <div tabIndex={0}>{children}</div>
    </Popover.Panel>
  );
};
